// pages/Roles.js
import React, { useState, useEffect } from 'react';
import {
  Table,Select,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  Tree,
  Divider,
  Tabs
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  SafetyOutlined,
  UserOutlined,
  KeyOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const Roles = () => {
  // Estados
  const [roles, setRoles] = useState([]);
  const [menusArbol, setMenusArbol] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentRol, setCurrentRol] = useState(null);
  const [selectedRol, setSelectedRol] = useState(null);
  const [selectedPermisos, setSelectedPermisos] = useState([]);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('roles');

  // Columnas para la tabla de roles
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 150,
      render: (nombre) => {
        let color = 'blue';
        let icon = <UserOutlined />;
        if (nombre === 'super_admin') {
          color = 'red';
          icon = <SafetyOutlined />;
        } else if (nombre === 'admin') {
          color = 'orange';
          icon = <TeamOutlined />;
        }
        return (
          <Tag color={color} icon={icon} style={{ fontSize: '14px', padding: '4px 12px' }}>
            {nombre}
          </Tag>
        );
      },
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      key: 'activo',
      width: 100,
      render: (activo) => {
        const esActivo = activo === 1 || activo === true;
        return esActivo ? (
          <Badge status="success" text="Activo" />
        ) : (
          <Badge status="error" text="Inactivo" />
        );
      },
      filters: [
        { text: 'Activo', value: true },
        { text: 'Inactivo', value: false },
      ],
      onFilter: (value, record) => {
        const esActivo = record.activo === 1 || record.activo === true;
        return esActivo === value;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Configurar Permisos">
            <Button
              type="default"
              icon={<KeyOutlined />}
              size="small"
              onClick={() => handleConfigurarPermisos(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
              disabled={record.nombre === 'super_admin'}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar rol?"
            description="Esta acción eliminará el rol permanentemente"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
            disabled={record.nombre === 'super_admin'}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                disabled={record.nombre === 'super_admin'}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchRoles();
    fetchMenusArbol();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/usuarios/roles/listar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando roles:', error);
      message.error('Error al cargar los roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchMenusArbol = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/usuarios/menus/arbol`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenusArbol(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando menús:', error);
    }
  };

  const fetchPermisosRol = async (rolId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/usuarios/permisos/${rolId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const permisos = response.data.data || [];
      setSelectedPermisos(permisos.map(p => p.id));
    } catch (error) {
      console.error('❌ Error cargando permisos:', error);
    }
  };

  const handleCreate = () => {
    setModalType('create');
    setCurrentRol(null);
    form.resetFields();
    form.setFieldsValue({ activo: true });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentRol(record);
    form.setFieldsValue({
      nombre: record.nombre,
      descripcion: record.descripcion,
      activo: record.activo === 1 || record.activo === true
    });
    setModalVisible(true);
  };

  const handleConfigurarPermisos = (record) => {
    setSelectedRol(record);
    fetchPermisosRol(record.id);
    setActiveTab('permisos');
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/usuarios/roles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRoles(roles.filter(r => r.id !== id));
      message.success('Rol eliminado correctamente');
    } catch (error) {
      console.error('❌ Error eliminando rol:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el rol');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      if (modalType === 'create') {
        await axios.post(
          `${BASE_URL}/api/usuarios/roles`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Rol creado correctamente');
      } else {
        await axios.put(
          `${BASE_URL}/api/usuarios/roles/${currentRol.id}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Rol actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchRoles();
      
    } catch (error) {
      console.error('❌ Error guardando rol:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleGuardarPermisos = async () => {
    if (!selectedRol) {
      message.warning('Seleccione un rol primero');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BASE_URL}/api/usuarios/permisos/${selectedRol.id}`,
        { permisos: selectedPermisos },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('Permisos actualizados correctamente');
    } catch (error) {
      console.error('❌ Error guardando permisos:', error);
      message.error('Error al guardar los permisos');
    } finally {
      setLoading(false);
    }
  };

  const onCheckPermisos = (checkedKeys) => {
    setSelectedPermisos(checkedKeys);
  };

  // Convertir árbol de menús para Tree component
  const convertToTreeData = (menus) => {
    return menus.map(menu => ({
      key: menu.id,
      title: (
        <span>
          {menu.menu_label}
          {menu.ruta && (
            <Text type="secondary" style={{ fontSize: '11px', marginLeft: 8 }}>
              ({menu.ruta})
            </Text>
          )}
        </span>
      ),
      children: menu.children ? convertToTreeData(menu.children) : undefined
    }));
  };

  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={8}>
        <Statistic 
          title="Total Roles" 
          value={roles.length}
          prefix={<TeamOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={8}>
        <Statistic 
          title="Activos" 
          value={roles.filter(r => r.activo === 1 || r.activo === true).length}
          suffix={`/ ${roles.length}`}
          prefix={<CheckCircleOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={8}>
        <Statistic 
          title="Menús disponibles" 
          value={menusArbol.length}
          prefix={<AppstoreOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  return (
    <PageContainer
      title="Gestión de Roles"
      subtitle={`${roles.length} roles configurados`}
      icon={<TeamOutlined />}
      loading={loading}
      onRefresh={fetchRoles}
      onCreate={handleCreate}
      badgeCount={roles.length}
      badgeStatus="processing"
      cardStyle={{
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
      }}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Roles" key="roles">
          {estadisticas}
          
          <Table
            columns={columns}
            dataSource={roles}
            rowKey="id"
            loading={loading}
            scroll={{ x: 800 }}
            style={{
              borderRadius: '8px',
              overflow: 'hidden'
            }}
            size="middle"
            locale={{
              emptyText: 'No se encontraron roles'
            }}
          />
        </TabPane>
        
        <TabPane tab="Permisos por Rol" key="permisos">
          <Card>
            <Row gutter={24}>
              <Col span={8}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Seleccionar Rol:</Text>
                </div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Seleccione un rol"
                  onChange={(rolId) => {
                    const rol = roles.find(r => r.id === rolId);
                    setSelectedRol(rol);
                    fetchPermisosRol(rolId);
                  }}
                  value={selectedRol?.id}
                  allowClear
                >
                  {roles.map(rol => (
                    <Option key={rol.id} value={rol.id}>
                      <Space>
                        {rol.nombre === 'super_admin' && <SafetyOutlined />}
                        {rol.nombre === 'admin' && <TeamOutlined />}
                        {rol.nombre === 'usuario' && <UserOutlined />}
                        <span>{rol.nombre}</span>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          - {rol.descripcion}
                        </Text>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={16}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong>Permisos del Rol:</Text>
                  <Button 
                    type="primary" 
                    onClick={handleGuardarPermisos}
                    disabled={!selectedRol}
                  >
                    Guardar Permisos
                  </Button>
                </div>
                {selectedRol ? (
                  <div style={{ 
                    border: '1px solid #f0f0f0', 
                    borderRadius: '8px', 
                    padding: '16px',
                    maxHeight: '500px',
                    overflow: 'auto'
                  }}>
                    <Tree
                      checkable
                      checkStrictly
                      defaultExpandAll
                      checkedKeys={selectedPermisos}
                      onCheck={onCheckPermisos}
                      treeData={convertToTreeData(menusArbol)}
                    />
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px', 
                    backgroundColor: '#fafafa',
                    borderRadius: '8px'
                  }}>
                    <Text type="secondary">Seleccione un rol para ver y configurar sus permisos</Text>
                  </div>
                )}
              </Col>
            </Row>
            
            <Divider />
            
            <Card size="small" style={{ backgroundColor: '#e6f7ff' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>💡 Información sobre roles y permisos:</strong>
                <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                  <li><strong>Super Admin:</strong> Rol con acceso total al sistema (no se puede editar ni eliminar)</li>
                  <li><strong>Admin:</strong> Rol con permisos administrativos, puede acceder a gestión de usuarios</li>
                  <li><strong>Usuario:</strong> Rol con acceso básico a funcionalidades operativas</li>
                  <li>Los permisos determinan qué menús y funcionalidades puede ver cada rol</li>
                  <li>Marque/desmarque los checkboxes en el árbol de menús para asignar permisos</li>
                </ul>
              </div>
            </Card>
          </Card>
        </TabPane>
      </Tabs>

      {/* Modal para crear/editar rol */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Rol' : 'Editar Rol'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={500}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="nombre"
            label="Nombre del Rol"
            rules={[
              { required: true, message: 'Por favor ingrese el nombre del rol' },
              { pattern: /^[a-z_]+$/, message: 'Solo letras minúsculas y guión bajo' }
            ]}
            tooltip="Identificador único del rol (ej: gerente, recepcionista)"
          >
            <Input 
              placeholder="ej: gerente, recepcionista"
              disabled={modalType === 'edit'}
            />
          </Form.Item>

          <Form.Item
            name="descripcion"
            label="Descripción"
            rules={[{ required: true, message: 'Por favor ingrese la descripción' }]}
          >
            <TextArea 
              rows={3}
              placeholder="Descripción de las responsabilidades del rol"
            />
          </Form.Item>

          <Form.Item
            name="activo"
            label="Estado"
            valuePropName="checked"
            tooltip="Rol activo puede ser asignado a usuarios"
          >
            <Switch
              checkedChildren="Activo"
              unCheckedChildren="Inactivo"
            />
          </Form.Item>

          <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
            <div style={{ fontSize: '13px', color: '#666' }}>
              <strong>📝 Nota:</strong>
              <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                <li>El nombre del rol debe ser único y en minúsculas</li>
                <li>Después de crear el rol, podrá asignarle permisos en la pestaña "Permisos por Rol"</li>
                <li>Los roles predeterminados (super_admin, admin, usuario) no se pueden editar</li>
              </ul>
            </div>
          </Card>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default Roles;